import React from 'react';
import Table from "src/componets/Table";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { ColumnsType } from "antd/es/table";
import { Button, ButtonEdit } from "src/componets/Buttons";
import { setEditId, BundleInterface } from "src/store/community/bundlesSlice";
import { EntityId } from "@reduxjs/toolkit";
import { formattedDate } from "src/helpers/DateHelpers";
import OptionalLink from "src/componets/OptionalLink";
import Pagination from "src/componets/Pagination";
import usePermissions from "src/hooks/usePermissions";

import { Avatar, Space, Tag } from "antd";
import { AndroidOutlined, AppleOutlined, BorderOutlined, CheckSquareOutlined } from "@ant-design/icons";
import {updateAppBundle} from "../../../../actions/community/appsActions";
import {selectAppById} from "../../../../store/community/appsSlice";
import {useParams} from "react-router-dom";

interface ListTableInterface {
  className?: string;
  limit: number;
}

const _ListTable = ({ className, limit }: ListTableInterface) => {
  const dispatch = useAppDispatch();
  const ids = useAppSelector(state => state.community.bundles.ids);
  const bundles = useAppSelector(state => state.community.bundles.entities);
  const count = useAppSelector(state => state.community.bundles.count);
  const data  = ids ? ids.map(id => bundles[id]) : [];
  const { app_id } = useParams();
  const app = useAppSelector(state => app_id ? selectAppById(state, app_id) : undefined);
  const { write_community } = usePermissions();

  function onClickEdit(id: EntityId) {
    dispatch(setEditId(id));
  }

  function onClickSetAppBundle(app_id: number, id: EntityId) {
    dispatch(updateAppBundle({ id: app_id, data: { bundle_id: id } }));
  }

  const AppButton = ({ app_id, id }: { app_id: number, id: EntityId }) => {
    let icon: React.FC;
    let className: string | undefined;

    if (app && app.main_bundle_id === id) {
      icon = CheckSquareOutlined;
      className = 'v-btn--success';
    } else {
      icon = BorderOutlined;
    }

    return <Button
      icon={icon}
      title='Main'
      onClick={() => onClickSetAppBundle(app_id, id)}
      className={className}
    />
  }

  const renderPlatformIcon = (platform: string) => {
    if (platform === 'ios') {
      return <AppleOutlined />
    } else {
      return <AndroidOutlined />
    }
  }

  const renderEnvLabel = (prod: boolean) => {
    if (prod) {
      return <Tag color="success">production</Tag>
    } else {
      return <Tag color="default">development</Tag>
    }
  }

  const columns: ColumnsType<BundleInterface> = [
    {
      key: 'icon_url',
      title: '',
      dataIndex: 'icon_url',
      render: (_, { icon_url }) => {
        return <Avatar
          src={icon_url}
          alt='Bundle icon'
          size={32}
          icon={<AppleOutlined />}
          shape='square'
        />
      },
      width: 50
    },

    {
      key: 'name',
      title: 'Name',
      render: (_, { name, platform, url }) => <OptionalLink link={url}>{renderPlatformIcon(platform)} {name}</OptionalLink>,
      width: 135,
      ellipsis: true
    },

    {
      key: 'version',
      title: 'Version',
      render: (_, { version }) => <small>{version}</small>,
      width: 65
    },

    {
      key: 'prod',
      title: 'Environment',
      render: (_, { prod }) => renderEnvLabel(prod),
      width: 125
    },

    {
      key: 'created_at',
      title: 'Created at',
      render: (_, { created_at }) => {
        return <small>{formattedDate(created_at).dateTime}</small>;
      },
      width: 125
    },

    {
      key: 'updated_at',
      title: 'Updated at',
      render: (_, { updated_at }) => {
        return <small>{formattedDate(updated_at).dateTime}</small>;
      },
      width: 125
    },

    ...(write_community ? [{
      key: 'action',
      title: 'Action',
      render: (_: any, { id, app_id}: BundleInterface) => {
        return (
          <Space size="middle">
            <ButtonEdit key={'button-edit'} onClick={() => onClickEdit(id)} />
            <AppButton app_id={app_id} id={id} />
          </Space>
        );
      },
      width: 100
    }] : []),
  ];

  return (
    <div className={className}>
      <Table
        rowKey='id'
        columns={columns}
        dataSource={data}
      />

      <Pagination className='v-mt-10' total={count} size={limit} />
    </div>
  )
}

export default _ListTable;
